import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { CommunityAuthor } from '@/shared/composables/community/profile';
import { userCookies } from '@/shared/constants/cookies';
import { customerLoyaltyPointsQuery } from '@/shared/graphql/storefront/queries/loyalty.queries';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { setCookie } from '@/shared/utils/cookie';
import { parseJwt } from '@/shared/utils/jwt';
import { getCustomerAccessToken } from '@/shared/utils/user';
import {
  hobbiiLocalStorage,
  hobbiiSessionStorage
} from '@/shared/utils/web-storage';

// TODO: Could this be determined by the creating service?
export type Customer = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
};

type OwnProfile = Omit<CommunityAuthor, 'is_following' | 'is_blocking'>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidCustomer = (data: any): data is Customer =>
  data &&
  typeof data.uuid === 'string' &&
  typeof data.first_name === 'string' &&
  typeof data.last_name === 'string' &&
  typeof data.email === 'string';

export const useUserStore = defineStore('user', () => {
  const pointsBalance = ref<number | null>(null);
  const _persistedToken = ref<string | null>(
    hobbiiLocalStorage.getItem(userCookies.C_ID)
  );

  const _token = computed<string | null>({
    set: (value: string | null) => {
      _persistedToken.value = value;
      if (value) {
        hobbiiLocalStorage.setItem(userCookies.C_ID, value);
      } else {
        hobbiiLocalStorage.removeItem(userCookies.C_ID);
        setCookie(userCookies.C_ID, '', -5);
      }
    },
    get: () => _persistedToken.value
  });

  const customer = computed<Customer | null>(() => {
    if (!_token.value) {
      return null;
    }
    const obj = parseJwt(_token.value);

    const customerData = {
      uuid: obj['cognito:username'],
      first_name: obj.name,
      last_name: obj.family_name,
      email: obj.email
    };

    if (isValidCustomer(customerData)) {
      return customerData;
    }

    console.error('Incomplete customer data from token');
    return null;
  });

  const authenticated = computed(() => !!customer.value);
  /**
   * @deprecated Use authenticated instead
   */
  const isLogged = computed(() => authenticated.value);

  const setCustomer = (token?: string) => {
    if (token) {
      _token.value = token;
      hobbiiSessionStorage.setItem(userCookies.C_ID, token);
    } else {
      cleanCustomer();
    }
  };

  const cleanCustomer = () => {
    _token.value = null;
    _communityProfile.value = null;
    hobbiiSessionStorage.removeItem(userCookies.C_ID);
  };

  const getCustomerPointsBalance = async () => {
    if (pointsBalance.value !== null) {
      return pointsBalance.value;
    }

    const { gateway, storefront } = await getApolloClients();
    const customerAccessToken = await getCustomerAccessToken(gateway);

    if (customerAccessToken) {
      const customerLoyaltyPointsResponse = await storefront.query({
        query: customerLoyaltyPointsQuery,
        variables: { customerAccessToken }
      });

      const loyaltyPoints =
        customerLoyaltyPointsResponse.data.customer?.loyaltyPoints?.value;

      if (!loyaltyPoints) return null;

      pointsBalance.value = Number(loyaltyPoints);
      return pointsBalance.value;
    }

    return null;
  };

  // TODO: Temp solution, while userStore & apolloClient is still tangled
  const _communityProfile = ref<OwnProfile | null>();
  const communityProfile = computed(() => _communityProfile.value);
  const setCommunityProfile = (profile: OwnProfile) => {
    _communityProfile.value = profile;
  };

  // ToS (ToC)
  const _termsOfServiceAgreed = ref<boolean | null>(null);
  const setTermsOfServiceAgreement = (agreed: boolean) => {
    _termsOfServiceAgreed.value = agreed;
    _showTermsOfServiceAgreementModal.value = false;
  };

  const _showTermsOfServiceAgreementModal = ref(false);
  const showingTermsOfServiceAgreementModal = computed(
    () => _showTermsOfServiceAgreementModal.value
  );

  const openAndAwaitTermsOfServiceAgreementModal = () => {
    _showTermsOfServiceAgreementModal.value = true;

    return new Promise((resolve) => {
      // watch for one change
      const stopWatching = watch(
        _showTermsOfServiceAgreementModal,
        (showing) => {
          if (!showing) {
            stopWatching();
            resolve(_termsOfServiceAgreed.value);
          }
        }
      );
    });
  };

  const closeTermsOfServiceAgreementModal = () => {
    _showTermsOfServiceAgreementModal.value = false;
  };

  return {
    authenticated,
    isLogged,
    customer,
    setCustomer,
    cleanCustomer,
    communityProfile,
    setCommunityProfile,
    openAndAwaitTermsOfServiceAgreementModal,
    showingTermsOfServiceAgreementModal,
    closeTermsOfServiceAgreementModal,
    setTermsOfServiceAgreement,
    getCustomerPointsBalance
  };
});
